/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Card, Stack } from '@mui/material';
import React from 'react';
import { useResizable } from 'react-resizable-layout';
import { ArrowDropDownDown } from '@opengeoweb/theme';

export interface ResizablePanelProps {
  name: string;
  minWidth?: number;
  maxWidth?: number;
  isOpen?: boolean;
  headerAction?: React.ReactElement;
  handleButtonComponent?: React.ReactElement;
  children?: React.ReactNode;
  onSetIsOpen?: (isOpen: boolean) => void;
  onSetCustomWidth?: (width: number) => void;
  onResizeStart?: () => void;
  onResizeEnd?: () => void;
  customWidth?: number;
  isDisplayed?: boolean;
  resizeOnViewCloseSignal?: number;
}

const PANEL_MIN_WIDTH = 24;

export const ResizablePanel = React.forwardRef<
  HTMLDivElement,
  ResizablePanelProps
>(
  (
    {
      name,
      minWidth = PANEL_MIN_WIDTH,
      maxWidth,
      isOpen,
      headerAction,
      handleButtonComponent,
      children,
      onSetIsOpen,
      onSetCustomWidth,
      onResizeStart,
      onResizeEnd,
      customWidth,
      isDisplayed,
      resizeOnViewCloseSignal,
    }: ResizablePanelProps,
    contentRef,
  ): React.JSX.Element => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const widthRef = React.useRef<number | null>(null);
    const isDraggingRef = React.useRef(false);
    const [autoSizing, setAutoSizing] = React.useState(false);
    const [growing, setGrowing] = React.useState(false);
    const growBasisRef = React.useRef<number>(minWidth);
    const previousResizeSignalRef = React.useRef(resizeOnViewCloseSignal);

    const {
      position: containerWidth,
      setPosition: setContainerWidth,
      separatorProps,
    } = useResizable({
      containerRef: containerRef as React.RefObject<HTMLDivElement>,
      axis: 'x',
      min: minWidth,
      max: maxWidth || undefined,
      initial: customWidth ?? minWidth,
      onResizeStart: () => {
        isDraggingRef.current = true;
        setAutoSizing(false);
        onResizeStart?.();
      },
      onResizeEnd: ({ position }) => {
        isDraggingRef.current = false;
        if (position <= minWidth) {
          widthRef.current = null;
          onSetIsOpen?.(false);
        } else {
          widthRef.current = position;
          onSetIsOpen?.(true);
          onSetCustomWidth?.(position);
        }
        // Fire after the open/close calls so consumers tracking the drag
        // lifecycle (e.g. a "resizing" flag) reset only once the close above
        // has been handled.
        onResizeEnd?.();
      },
    });

    const openPanel = (): void => {
      const targetWidth = widthRef.current ?? customWidth ?? null;
      if (targetWidth != null) {
        setAutoSizing(false);
        setContainerWidth(Math.max(targetWidth, minWidth));
        onSetIsOpen?.(true);
        return;
      }
      setAutoSizing(true);
      onSetIsOpen?.(true);
    };

    const closePanel = React.useCallback(() => {
      setContainerWidth(minWidth);
      onSetIsOpen?.(false);
      setAutoSizing(false);
    }, [minWidth, setContainerWidth, onSetIsOpen]);

    React.useEffect(() => {
      // While dragging, useResizable owns containerWidth based on the pointer.
      // Running the width-syncing branches here would jump away from the user's
      // drag position, so skip this effect until the drag ends.
      if (isDraggingRef.current) {
        return;
      }
      if (isOpen) {
        const targetWidth = widthRef.current ?? customWidth ?? null;
        if (targetWidth != null) {
          setAutoSizing(false);
          setContainerWidth(Math.max(targetWidth, minWidth));
        } else {
          setAutoSizing(true);
        }
      } else {
        setContainerWidth(minWidth);
        setAutoSizing(false);
      }
    }, [isOpen, minWidth, setContainerWidth, customWidth]);

    // Flip open/closed state during an active drag as the width crosses the
    // minWidth threshold, so the panel can close and re-open within a
    // single drag gesture without waiting for pointer release.
    React.useEffect(() => {
      if (!isDraggingRef.current) {
        return;
      }
      if (isOpen && containerWidth <= minWidth) {
        onSetIsOpen?.(false);
      } else if (!isOpen && containerWidth > minWidth) {
        onSetIsOpen?.(true);
      }
    }, [isOpen, containerWidth, minWidth, onSetIsOpen]);

    React.useEffect(() => {
      const viewCloseSignalChanged =
        previousResizeSignalRef.current !== resizeOnViewCloseSignal;
      if (!viewCloseSignalChanged) {
        return;
      }
      previousResizeSignalRef.current = resizeOnViewCloseSignal;

      const panelCanReclaimFreedSpace =
        isOpen && !autoSizing && !isDraggingRef.current;
      if (panelCanReclaimFreedSpace) {
        growBasisRef.current = Math.max(containerWidth, minWidth);
        setGrowing(true);
      }
    }, [resizeOnViewCloseSignal, isOpen, autoSizing, containerWidth, minWidth]);

    React.useLayoutEffect(() => {
      if (autoSizing) {
        return;
      }
      if (isDraggingRef.current) {
        return;
      }
      if (!isOpen && containerWidth > minWidth) {
        setContainerWidth(minWidth);
        return;
      }
      if (isOpen && containerWidth <= minWidth) {
        setAutoSizing(true);
      }
    }, [autoSizing, containerWidth, isOpen, minWidth, setContainerWidth]);

    React.useLayoutEffect(() => {
      if (!autoSizing || !containerRef.current) {
        return;
      }
      let raf1 = 0;
      let raf2 = 0;

      const measureContainerWidth = (): void => {
        if (!containerRef.current) {
          return;
        }
        const { width } = containerRef.current.getBoundingClientRect();
        const calculatedWidth = Math.max(Math.round(width), minWidth);
        setContainerWidth(calculatedWidth);
        widthRef.current = calculatedWidth;
        setAutoSizing(false);
      };

      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(measureContainerWidth);
      });

      // eslint-disable-next-line consistent-return
      return (): void => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }, [autoSizing, minWidth, setContainerWidth]);

    React.useLayoutEffect(() => {
      if (!growing || !containerRef.current) {
        return;
      }
      let growRaf1 = 0;
      let growRaf2 = 0;

      const lockGrownWidth = (): void => {
        if (!containerRef.current) {
          return;
        }
        const { width } = containerRef.current.getBoundingClientRect();
        const lockedWidth = Math.max(Math.round(width), minWidth);
        setContainerWidth(lockedWidth);
        widthRef.current = lockedWidth;
        setGrowing(false);
      };

      growRaf1 = requestAnimationFrame(() => {
        growRaf2 = requestAnimationFrame(lockGrownWidth);
      });

      // eslint-disable-next-line consistent-return
      return (): void => {
        cancelAnimationFrame(growRaf1);
        cancelAnimationFrame(growRaf2);
      };
    }, [growing, minWidth, setContainerWidth]);

    const cardStyleWhenAuto = {
      display: isDisplayed ? 'block' : 'none',
      width: 'auto',
      flex: '1 1 100%',
      minWidth: 0,
      maxWidth: maxWidth || '100%',
      boxSizing: 'border-box',
    };
    const cardStyleWhenLocked = {
      display: isDisplayed ? 'block' : 'none',
      width: containerWidth,
      flex: '0 1 auto',
      minWidth,
      maxWidth: maxWidth || '100%',
      boxSizing: 'border-box',
    };
    const cardStyleWhenGrowing = {
      display: isDisplayed ? 'block' : 'none',
      width: 'auto',
      flex: `${growBasisRef.current} 1 0px`,
      minWidth,
      maxWidth: maxWidth || '100%',
      boxSizing: 'border-box',
    };

    const getActiveCardStyle = (): Record<string, unknown> => {
      if (growing) {
        return cardStyleWhenGrowing;
      }
      if (autoSizing) {
        return cardStyleWhenAuto;
      }
      return cardStyleWhenLocked;
    };

    return (
      <Card
        className="sortable-item"
        data-autosizing={autoSizing ? 'true' : 'false'}
        data-growing={growing ? 'true' : 'false'}
        data-testid="resizable-panel"
        ref={containerRef}
        sx={{
          height: '100%',
          position: 'relative',
          ...getActiveCardStyle(),
        }}
      >
        <Box ref={contentRef} sx={{ height: '100%', width: '100%' }}>
          {!isOpen && (
            <Box
              data-testid="closed-panel-header"
              onClick={openPanel}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                writingMode: 'vertical-lr',
                transform: 'rotate(180deg)',
                paddingBottom: '10px',
                cursor: 'pointer',
              }}
            >
              {name}
              <ArrowDropDownDown sx={{ transform: 'rotate(90deg)' }} />
              <Box onClick={(event): void => event.stopPropagation()}>
                {handleButtonComponent}
              </Box>
            </Box>
          )}
          {!!isOpen && (
            <Box
              data-testid="open-panel-header"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <Stack sx={{ height: '100%' }}>
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    flex: '0 0 auto',
                  }}
                >
                  <Box
                    onClick={(event): void => event.stopPropagation()}
                    sx={{ paddingTop: '8px' }}
                  >
                    {handleButtonComponent}
                  </Box>
                  <Box
                    onClick={closePanel}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      paddingRight: '10px',
                      overflow: 'hidden',
                      marginTop: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    <Box onClick={(event): void => event.stopPropagation()}>
                      {!!headerAction && headerAction}
                    </Box>
                    <Box
                      sx={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        marginLeft: '16px',
                        height: '100%',
                        paddingTop: '3px',
                      }}
                    >
                      {name}
                    </Box>
                    <ArrowDropDownDown />
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'stretch',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                  }}
                >
                  {children}
                </Box>
              </Stack>
            </Box>
          )}
          <Box
            data-testid="resizable-panel-drag-handle"
            {...separatorProps}
            sx={{
              height: '100%',
              position: 'absolute',
              right: 0,
              top: 0,
              width: '5px',
              cursor: 'col-resize',
            }}
          />
        </Box>
      </Card>
    );
  },
);

ResizablePanel.displayName = 'ResizablePanel';
